import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  afterNextRender,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import {
  _UiBarRect,
  UiBarChartBarEvent,
  UiBarChartCategory,
  UiBarChartLabelMode,
  UiBarChartSeries,
  UiBarChartVariant,
} from './bar-chart.types';

/** @internal Tick del eje Y calculado */
interface YTick { value: number; label: string; y: number; }
/** @internal Tick del eje X calculado */
interface XTick { label: string; x: number; }
interface TooltipSize { width: number; height: number; }
interface TooltipBounds { left: number; top: number; width: number; height: number; }
interface TooltipPointer {
  clientX: number;
  clientY: number;
  wrap: HTMLElement;
}

const DEFAULT_PALETTE = [
  '#4169e2', '#f97316', '#22c55e', '#8b5cf6',
  '#eab308', '#ef4444', '#06b6d4', '#7b96f5',
];

// Márgenes internos del SVG
const M = { top: 20, right: 20, bottom: 48, left: 52 } as const;

/**
 * Organismo: gráfico de barras presentacional.
 *
 * Implementado con SVG nativo — sin dependencias de charting externas.
 * Variantes: simple · grouped · stacked · horizontal · negative.
 *
 * @example
 * <ui-bar-chart
 *   [series]="mySeries"
 *   [categories]="myCategories"
 *   variant="grouped"
 *   (barClick)="onBar($event)"
 * />
 */
@Component({
  selector: 'ui-bar-chart',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet],
  templateUrl: './bar-chart.component.html',
  styleUrl: './bar-chart.component.scss',
  host: {
    '[class.ui-bar-chart-host--fill]': 'fillContainer()',
  },
})
export class UiBarChartComponent {
  // ── Inputs ──────────────────────────────────────────────────────────────
  /** Series de datos; una serie = un conjunto de colores + valores */
  readonly series = input<UiBarChartSeries[]>([]);
  /** Etiquetas del eje principal */
  readonly categories = input<UiBarChartCategory[]>([]);
  /** Forma visual del gráfico */
  readonly variant = input<UiBarChartVariant>('simple');
  /** Ancho total del SVG en píxeles */
  readonly width = input<number>(480);
  /** Alto total del SVG en píxeles */
  readonly height = input<number>(300);
  /** Ajusta el SVG al ancho y alto disponibles del contenedor. */
  readonly fillContainer = input<boolean>(false);
  /** Radio de esquinas de las barras en píxeles */
  readonly barRadius = input<number>(4);
  /** Factor de ancho para barras `simple`. Rango efectivo: 0.1 a 1. */
  readonly barWidthScale = input<number>(1);
  /** Ancho exacto opcional de las barras `simple`, en píxeles. */
  readonly barWidth = input<number | null>(null);
  /** Separación exacta opcional entre barras `simple`, en píxeles. */
  readonly categoryGap = input<number | null>(null);
  /** Factor de separación entre categorías `simple`. Rango efectivo: 0.2 a 1. */
  readonly categorySpacingScale = input<number>(1);
  /** Título visible sobre el gráfico */
  readonly title = input<string>('');
  /** Etiqueta del eje Y (o X en horizontal) */
  readonly yLabel = input<string>('');
  /** Etiqueta del eje X (o Y en horizontal) */
  readonly xLabel = input<string>('');
  /** Dónde mostrar los valores */
  readonly labelMode = input<UiBarChartLabelMode>('tooltip-only');
  /** Formatea valores en tooltip, etiquetas visibles y texto accesible. */
  readonly valueFormatter = input<(value: number) => string>(
    (value) => String(value),
  );
  /** Formato exclusivo del valor mostrado en el tooltip. Si falta, usa `valueFormatter`. */
  readonly tooltipValueFormatter = input<
    ((event: UiBarChartBarEvent) => string) | null
  >(null);
  /** Sustituye el nombre de la serie en el encabezado del tooltip. */
  readonly tooltipLabelFormatter = input<
    ((event: UiBarChartBarEvent) => string) | null
  >(null);
  /** Mostrar líneas de cuadrícula */
  readonly showGrid = input<boolean>(true);
  /** Mostrar leyenda de series */
  readonly showLegend = input<boolean>(true);
  /** Skeleton de carga */
  readonly loading = input<boolean>(false);
  /** Animación de barras al montar */
  readonly animated = input<boolean>(true);

  // ── Outputs ─────────────────────────────────────────────────────────────
  /** Emite al hacer click en una barra */
  readonly barClick = output<UiBarChartBarEvent>();
  /** Emite al hover; null cuando el cursor sale */
  readonly barHover = output<UiBarChartBarEvent | null>();

  // ── Estado UI interno ────────────────────────────────────────────────────
  readonly hoveredKey  = signal<string | null>(null);
  readonly activeKey   = signal<string | null>(null);
  readonly tooltipVisible = signal(false);
  readonly tooltipX    = signal(0);
  readonly tooltipY    = signal(0);
  readonly tooltipBar  = signal<_UiBarRect | null>(null);
  readonly tooltipReady = signal(false);

  private tooltipSize: TooltipSize | null = null;
  private tooltipBounds: TooltipBounds | null = null;
  private tooltipPointer: TooltipPointer | null = null;
  private tooltipFrame: number | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly measuredWidth = signal<number | null>(null);
  private readonly measuredHeight = signal<number | null>(null);

  constructor() {
    const destroyRef = inject(DestroyRef);
    destroyRef.onDestroy(() => {
      this.cancelTooltipFrame();
      this.resizeObserver?.disconnect();
    });
    afterNextRender(() => {
      this.syncContainerSize();
      this.resizeObserver = new ResizeObserver(() => this.syncContainerSize());
      this.resizeObserver.observe(this.host.nativeElement);
    });
  }

  // ── Área de dibujo ───────────────────────────────────────────────────────
  protected readonly chartWidth = computed(() =>
    this.fillContainer() ? this.measuredWidth() ?? this.width() : this.width(),
  );
  protected readonly chartHeight = computed(() =>
    this.fillContainer() ? this.measuredHeight() ?? this.height() : this.height(),
  );
  protected readonly chartMargin = computed(() => {
    if (this.variant() !== 'horizontal') return M;
    const longestCategory = this.categories().reduce(
      (max, category) => Math.max(max, category.label.length),
      0,
    );
    const longestValue = this.labelMode() === 'top'
      ? this.series().reduce((max, series) => {
          for (const value of series.data) {
            max = Math.max(max, this.formatValue(value).length);
          }
          return max;
        }, 1)
      : 0;
    return {
      ...M,
      left: Math.max(M.left, Math.ceil(14 + longestCategory * 6.8)),
      right: Math.max(M.right, longestValue ? 18 + longestValue * 8 : M.right),
    };
  });
  readonly plotW = computed(() =>
    Math.max(1, this.chartWidth() - this.chartMargin().left - this.chartMargin().right),
  );
  readonly plotH = computed(() =>
    Math.max(1, this.chartHeight() - this.chartMargin().top - this.chartMargin().bottom),
  );

  // ── Escala Y ─────────────────────────────────────────────────────────────
  readonly yDomain = computed<{ min: number; max: number }>(() => {
    const s = this.series();
    if (!s.length) return { min: 0, max: 100 };
    const v = this.variant();
    const allValues = s.flatMap(sr => sr.data);
    if (v === 'stacked') {
      const cats = this.categories().length || (s[0]?.data.length ?? 0);
      const posMax = Array.from({ length: cats }, (_, i) =>
        s.reduce((sum, sr) => sum + Math.max(0, sr.data[i] ?? 0), 0)
      );
      const negMin = Array.from({ length: cats }, (_, i) =>
        s.reduce((sum, sr) => sum + Math.min(0, sr.data[i] ?? 0), 0)
      );
      return { min: Math.min(0, ...negMin), max: Math.max(...posMax) };
    }
    const min = v === 'negative' ? Math.min(0, ...allValues) : 0;
    return { min, max: Math.max(0, ...allValues) };
  });

  readonly yTicks = computed<YTick[]>(() => {
    const { min, max } = this.yDomain();
    const plotH = this.plotH();
    const isHoriz = this.variant() === 'horizontal';
    if (isHoriz) return []; // en horizontal los ticks Y son las categorías
    const ticks = niceLinear(min, max, 5);
    return ticks.map(v => ({
      value: v,
      label: formatNum(v),
      y: plotH - ((v - min) / (max - min)) * plotH,
    }));
  });

  readonly xTicks = computed<XTick[]>(() => {
    const cats = this.categories();
    const plotW = this.plotW();
    const n = cats.length;
    if (!n) return [];
    const usesCustomSpacing =
      this.variant() === 'simple' || this.variant() === 'negative';
    const spacingScale = usesCustomSpacing
      ? Math.min(1, Math.max(0.2, this.categorySpacingScale()))
      : 1;
    const exactBarWidth = this.barWidth();
    const exactGap = this.categoryGap();
    const usesExactGap =
      usesCustomSpacing && exactBarWidth !== null && exactGap !== null;
    const step = usesExactGap
      ? Math.max(0.1, exactBarWidth) + Math.max(0, exactGap)
      : (plotW / n) * spacingScale;
    const start = (plotW - step * n) / 2;
    return cats.map((c, i) => ({
      label: c.label,
      x: start + (i + 0.5) * step,
    }));
  });

  // ── Barras calculadas ────────────────────────────────────────────────────
  readonly bars = computed<_UiBarRect[]>(() => {
    const s    = this.series();
    const cats = this.categories();
    if (!s.length || !cats.length) return [];

    const v = this.variant();
    switch (v) {
      case 'simple':     return this.buildSimple(s, cats);
      case 'grouped':    return this.buildGrouped(s, cats);
      case 'stacked':    return this.buildStacked(s, cats);
      case 'horizontal': return this.buildHorizontal(s, cats);
      case 'negative':   return this.buildSimple(s, cats); // misma lógica, min puede ser <0
      default:           return this.buildSimple(s, cats);
    }
  });

  /** Stacked: un grupo por categoría para animar la barra completa. */
  readonly stackedGroups = computed(() => {
    if (this.variant() !== 'stacked') return [];
    const groups = new Map<
      number,
      { catIdx: number; animDelay: string; bars: _UiBarRect[] }
    >();
    for (const bar of this.bars()) {
      const current = groups.get(bar.catIdx) ?? {
        catIdx: bar.catIdx,
        animDelay: bar.animDelay,
        bars: [],
      };
      current.bars.push(bar);
      groups.set(bar.catIdx, current);
    }
    return [...groups.values()].sort((a, b) => a.catIdx - b.catIdx);
  });

  // Zero line Y coordinate (for negative variant)
  readonly zeroY = computed(() => {
    const { min, max } = this.yDomain();
    return this.plotH() - ((0 - min) / (max - min)) * this.plotH();
  });

  // ── Event handlers ───────────────────────────────────────────────────────
  onBarEnter(bar: _UiBarRect): void {
    this.resetTooltipPosition();
    this.hoveredKey.set(barKey(bar));
    this.tooltipBar.set(bar);
    this.tooltipVisible.set(true);
    this.barHover.emit(this.toEvent(bar));
  }

  onBarLeave(): void {
    this.hoveredKey.set(null);
    this.tooltipVisible.set(false);
    this.resetTooltipPosition();
    this.barHover.emit(null);
  }

  onCanvasLeave(): void {
    this.tooltipVisible.set(false);
    this.resetTooltipPosition();
  }

  onBarClick(bar: _UiBarRect): void {
    const k = barKey(bar);
    this.activeKey.set(this.activeKey() === k ? null : k);
    this.barClick.emit(this.toEvent(bar));
  }

  onMouseMove(event: MouseEvent): void {
    if (!this.tooltipVisible()) return;
    this.tooltipPointer = {
      clientX: event.clientX,
      clientY: event.clientY,
      wrap: event.currentTarget as HTMLElement,
    };

    if (this.tooltipSize && this.tooltipBounds) {
      this.updateTooltipPosition();
      return;
    }

    if (this.tooltipFrame !== null) return;

    this.tooltipFrame = requestAnimationFrame(() => {
      this.tooltipFrame = null;
      if (!this.tooltipVisible() || !this.tooltipPointer) return;

      const tooltip =
        this.tooltipPointer.wrap.querySelector<HTMLElement>(
          '.ui-bar-chart__tooltip',
        );
      if (!tooltip) return;

      const rect = this.tooltipPointer.wrap.getBoundingClientRect();
      this.tooltipSize = {
        width: tooltip.offsetWidth,
        height: tooltip.offsetHeight,
      };
      this.tooltipBounds = {
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
      };
      this.updateTooltipPosition();
      this.tooltipReady.set(true);
    });
  }

  private updateTooltipPosition(): void {
    if (!this.tooltipPointer || !this.tooltipSize || !this.tooltipBounds) return;

    const gap = 16;
    const verticalGap = 12;
    const cursorX = this.tooltipPointer.clientX - this.tooltipBounds.left;
    const cursorY = this.tooltipPointer.clientY - this.tooltipBounds.top;
    const { width, height } = this.tooltipSize;
    const maxX = Math.max(0, this.tooltipBounds.width - width);
    const maxY = Math.max(0, this.tooltipBounds.height - height);
    const rightX = cursorX + gap;
    const leftX = cursorX - width - gap;
    const nextX =
      rightX + width > this.tooltipBounds.width ? leftX : rightX;
    const aboveY = cursorY - height - verticalGap;
    const belowY = cursorY + verticalGap;
    const nextY = aboveY >= 0 ? aboveY : belowY;

    this.tooltipX.set(Math.min(Math.max(0, nextX), maxX));
    this.tooltipY.set(Math.min(Math.max(0, nextY), maxY));
  }

  private resetTooltipPosition(): void {
    this.cancelTooltipFrame();
    this.tooltipSize = null;
    this.tooltipBounds = null;
    this.tooltipPointer = null;
    this.tooltipReady.set(false);
    this.tooltipX.set(0);
    this.tooltipY.set(0);
  }

  private cancelTooltipFrame(): void {
    if (this.tooltipFrame === null) return;
    cancelAnimationFrame(this.tooltipFrame);
    this.tooltipFrame = null;
  }

  // ── Template helpers ─────────────────────────────────────────────────────
  protected barOpacity(bar: _UiBarRect): number {
    const hk = this.hoveredKey();
    if (!hk) return 1;
    return hk === barKey(bar) ? 1 : 0.45;
  }

  protected isActive(bar: _UiBarRect): boolean {
    return this.activeKey() === barKey(bar);
  }

  protected seriesColor(idx: number): string {
    return this.series()[idx]?.color ?? DEFAULT_PALETTE[idx % DEFAULT_PALETTE.length];
  }

  protected showLabel(bar: _UiBarRect): boolean {
    const lm = this.labelMode();
    if (lm === 'none' || lm === 'tooltip-only') return false;
    if (lm === 'inside') return bar.h > 22 || bar.w > 30;
    return true; // 'top'
  }

  protected formatValue(value: number): string {
    return this.valueFormatter()(value);
  }

  protected formatTooltipValue(bar: _UiBarRect): string {
    return this.tooltipValueFormatter()?.(this.toEvent(bar)) ??
      this.formatValue(bar.value);
  }

  protected formatTooltipLabel(bar: _UiBarRect): string {
    return this.tooltipLabelFormatter()?.(this.toEvent(bar)) ??
      this.series()[bar.seriesIdx]?.label ??
      '';
  }

  /**
   * Genera el path SVG de cada barra con redondeado SELECTIVO:
   * solo la punta visible (cima en vertical, extremo derecho en horizontal).
   *
   * Radio adaptativo:
   *  - Si la dimensión mínima de la barra < barRadius × 4 → r = 2px (evita efecto píldora)
   *  - En caso contrario → r = barRadius (limitado a la mitad del lado más corto)
   *
   * Stacked:
   *  - Solo el segmento marcado como cap (roundTop / roundBottom) recibe esquinas redondeadas
   *  - El resto de segmentos son rectángulos planos para evitar huecos entre capas
   */
  protected barPath(bar: _UiBarRect): string {
    const { x, y, w, h, value } = bar;
    const v = this.variant();
    const configR = this.barRadius();

    // Radio adaptativo: barras muy delgadas reciben 2px en lugar del radio configurado
    const minDim = Math.min(w, h);
    const r = minDim < configR * 4
      ? Math.min(2, minDim / 2)
      : Math.min(configR, minDim / 2);

    // ── Stacked: solo caps del extremo reciben redondeado ─────────────────────
    if (v === 'stacked') {
      const rt = bar.roundTop    ?? false;
      const rb = bar.roundBottom ?? false;

      if (!rt && !rb) {
        return `M ${x} ${y} H ${x + w} V ${y + h} H ${x} Z`;
      }
      // Top cap (segmento superior positivo)
      if (rt && !rb) {
        return r < 0.5
          ? `M ${x} ${y} H ${x + w} V ${y + h} H ${x} Z`
          : [
              `M ${x + r} ${y}`,
              `H ${x + w - r}`,
              `Q ${x + w} ${y} ${x + w} ${y + r}`,
              `V ${y + h}`,
              `H ${x}`,
              `V ${y + r}`,
              `Q ${x} ${y} ${x + r} ${y} Z`,
            ].join(' ');
      }
      // Bottom cap (segmento inferior negativo)
      if (!rt && rb) {
        return r < 0.5
          ? `M ${x} ${y} H ${x + w} V ${y + h} H ${x} Z`
          : [
              `M ${x} ${y}`,
              `H ${x + w}`,
              `V ${y + h - r}`,
              `Q ${x + w} ${y + h} ${x + w - r} ${y + h}`,
              `H ${x + r}`,
              `Q ${x} ${y + h} ${x} ${y + h - r} Z`,
            ].join(' ');
      }
      // Ambos (segmento único en la categoría)
      return r < 0.5
        ? `M ${x} ${y} H ${x + w} V ${y + h} H ${x} Z`
        : [
            `M ${x + r} ${y}`,
            `H ${x + w - r}`,
            `Q ${x + w} ${y} ${x + w} ${y + r}`,
            `V ${y + h - r}`,
            `Q ${x + w} ${y + h} ${x + w - r} ${y + h}`,
            `H ${x + r}`,
            `Q ${x} ${y + h} ${x} ${y + h - r}`,
            `V ${y + r}`,
            `Q ${x} ${y} ${x + r} ${y} Z`,
          ].join(' ');
    }

    // ── Sin radio mínimo: rectángulo plano ────────────────────────────────────
    if (r < 0.5) {
      return `M ${x} ${y} H ${x + w} V ${y + h} H ${x} Z`;
    }

    // ── Horizontal: redondear solo el extremo derecho ─────────────────────────
    if (v === 'horizontal') {
      return [
        `M ${x} ${y}`,
        `H ${x + w - r}`,
        `Q ${x + w} ${y} ${x + w} ${y + r}`,
        `V ${y + h - r}`,
        `Q ${x + w} ${y + h} ${x + w - r} ${y + h}`,
        `H ${x} Z`,
      ].join(' ');
    }

    // ── Vertical negativo: redondear solo la parte inferior (punta hacia abajo)
    if (value < 0) {
      return [
        `M ${x} ${y}`,
        `H ${x + w}`,
        `V ${y + h - r}`,
        `Q ${x + w} ${y + h} ${x + w - r} ${y + h}`,
        `H ${x + r}`,
        `Q ${x} ${y + h} ${x} ${y + h - r} Z`,
      ].join(' ');
    }

    // ── Vertical positivo (simple · grouped · negative-positive): solo cima ───
    return [
      `M ${x + r} ${y}`,
      `H ${x + w - r}`,
      `Q ${x + w} ${y} ${x + w} ${y + r}`,
      `V ${y + h}`,
      `H ${x}`,
      `V ${y + r}`,
      `Q ${x} ${y} ${x + r} ${y} Z`,
    ].join(' ');
  }

  // ── Build helpers ────────────────────────────────────────────────────────
  private buildSimple(s: UiBarChartSeries[], cats: UiBarChartCategory[]): _UiBarRect[] {
    const { min, max } = this.yDomain();
    const plotW = this.plotW();
    const plotH = this.plotH();
    const n = cats.length;
    const gap = plotW * 0.18 / n;
    const baseWidth = (plotW / n) - gap * 2;
    const widthScale = Math.min(1, Math.max(0.1, this.barWidthScale()));
    const spacingScale = Math.min(1, Math.max(0.2, this.categorySpacingScale()));
    const requestedWidth = this.barWidth();
    const bw = requestedWidth === null
      ? baseWidth * widthScale
      : Math.max(0.1, requestedWidth);
    const requestedGap = this.categoryGap();
    const step = requestedGap === null
      ? (plotW / n) * spacingScale
      : bw + Math.max(0, requestedGap);
    const start = (plotW - step * n) / 2;
    const pal = this.pal(s);
    const zeroY = plotH - ((0 - min) / (max - min)) * plotH;
    const rects: _UiBarRect[] = [];
    let delay = 0;

    cats.forEach((_, ci) => {
      s.forEach((sr, si) => {
        const val = sr.data[ci] ?? 0;
        const barH = Math.abs(((val - 0) / (max - min)) * plotH);
        const x = start + ci * step + (step - bw) / 2;
        const y = val >= 0 ? zeroY - barH : zeroY;
        rects.push({
          x, y, w: bw, h: barH,
          value: val, seriesIdx: si, catIdx: ci,
          color: sr.colors?.[ci] ?? sr.color ?? pal[si % pal.length],
          labelX: x + bw / 2,
          labelY: val >= 0 ? y - 5 : y + barH + 12,
          labelAnchor: 'middle',
          animDelay: `${delay * 35}ms`,
        });
        delay++;
      });
    });
    return rects;
  }

  private buildGrouped(s: UiBarChartSeries[], cats: UiBarChartCategory[]): _UiBarRect[] {
    const { min, max } = this.yDomain();
    const plotW = this.plotW();
    const plotH = this.plotH();
    const n  = cats.length;
    const ns = s.length;
    const groupW = plotW / n;
    const gap    = groupW * 0.15;
    const innerGap = 2;
    const bw = (groupW - gap * 2 - innerGap * (ns - 1)) / ns;
    const pal = this.pal(s);
    const zeroY = plotH;
    const rects: _UiBarRect[] = [];
    let delay = 0;

    cats.forEach((_, ci) => {
      s.forEach((sr, si) => {
        const val = sr.data[ci] ?? 0;
        const barH = ((val - min) / (max - min)) * plotH;
        const x = ci * groupW + gap + si * (bw + innerGap);
        const y = zeroY - barH;
        rects.push({
          x, y, w: bw, h: barH,
          value: val, seriesIdx: si, catIdx: ci,
          color: sr.colors?.[ci] ?? sr.color ?? pal[si % pal.length],
          labelX: x + bw / 2,
          labelY: y - 5,
          labelAnchor: 'middle',
          animDelay: `${delay * 28}ms`,
        });
        delay++;
      });
    });
    return rects;
  }

  private buildStacked(s: UiBarChartSeries[], cats: UiBarChartCategory[]): _UiBarRect[] {
    const { min, max } = this.yDomain();
    const plotW = this.plotW();
    const plotH = this.plotH();
    const n  = cats.length;
    const gap = plotW * 0.15 / n;
    const bw  = (plotW / n) - gap * 2;
    const pal = this.pal(s);
    const rects: _UiBarRect[] = [];

    cats.forEach((_, ci) => {
      let posY = plotH - ((0 - min) / (max - min)) * plotH;
      let negY = posY;
      s.forEach((sr, si) => {
        const val = sr.data[ci] ?? 0;
        const barH = Math.abs((val / (max - min)) * plotH);
        const x = ci * (plotW / n) + gap;
        let y: number;
        if (val >= 0) { y = posY - barH; posY = y; }
        else          { y = negY;        negY += barH; }
        rects.push({
          x, y, w: bw, h: barH,
          value: val, seriesIdx: si, catIdx: ci,
          color: sr.colors?.[ci] ?? sr.color ?? pal[si % pal.length],
          labelX: x + bw / 2,
          labelY: y + barH / 2,
          labelAnchor: 'middle',
          animDelay: `${ci * 30}ms`,
        });
      });
    });

    // Marcar los segmentos extremos por categoría para redondeo selectivo:
    // - roundTop    → segmento más alto del stack positivo (menor y)
    // - roundBottom → segmento más bajo del stack negativo (mayor y+h)
    const byCat = new Map<number, _UiBarRect[]>();
    for (const r of rects) {
      const arr = byCat.get(r.catIdx) ?? [];
      arr.push(r);
      byCat.set(r.catIdx, arr);
    }
    for (const group of byCat.values()) {
      const pos = group.filter(r => r.value >= 0);
      const neg = group.filter(r => r.value < 0);
      if (pos.length) {
        pos.reduce((a, b) => (a.y < b.y ? a : b)).roundTop = true;
      }
      if (neg.length) {
        neg.reduce((a, b) => ((a.y + a.h) > (b.y + b.h) ? a : b)).roundBottom = true;
      }
    }

    return rects;
  }

  private buildHorizontal(s: UiBarChartSeries[], cats: UiBarChartCategory[]): _UiBarRect[] {
    const allVals = s.flatMap(sr => sr.data);
    const maxVal  = Math.max(0, ...allVals);
    const plotW   = this.plotW();
    const plotH   = this.plotH();
    const n  = cats.length;
    const ns = s.length;
    const groupH = plotH / n;
    const gap     = groupH * 0.18;
    const innerGap = ns > 1 ? 2 : 0;
    const bh = (groupH - gap * 2 - innerGap * (ns - 1)) / ns;
    const pal = this.pal(s);
    const rects: _UiBarRect[] = [];
    let delay = 0;

    cats.forEach((_, ci) => {
      s.forEach((sr, si) => {
        const val = sr.data[ci] ?? 0;
        const bw  = (val / maxVal) * plotW;
        const x   = 0;
        const y   = ci * groupH + gap + si * (bh + innerGap);
        rects.push({
          x, y, w: bw, h: bh,
          value: val, seriesIdx: si, catIdx: ci,
          color: sr.colors?.[ci] ?? sr.color ?? pal[si % pal.length],
          labelX: bw + 6,
          labelY: y + bh / 2,
          labelAnchor: 'start',
          animDelay: `${delay * 30}ms`,
        });
        delay++;
      });
    });
    return rects;
  }

  private pal(s: UiBarChartSeries[]): string[] {
    return s.map((sr, i) => sr.color ?? DEFAULT_PALETTE[i % DEFAULT_PALETTE.length]);
  }

  private syncContainerSize(): void {
    if (!this.fillContainer()) return;
    const { clientWidth, clientHeight } = this.host.nativeElement;
    const width = Math.floor(clientWidth);
    const height = Math.floor(clientHeight);
    if (width > 0 && width !== this.measuredWidth()) this.measuredWidth.set(width);
    if (height > 0 && height !== this.measuredHeight()) this.measuredHeight.set(height);
  }

  private toEvent(bar: _UiBarRect): UiBarChartBarEvent {
    return {
      series: this.series()[bar.seriesIdx],
      categoryIndex: bar.catIdx,
      value: bar.value,
    };
  }
}

// ── Utilidades puras ─────────────────────────────────────────────────────────
function barKey(b: _UiBarRect): string { return `${b.seriesIdx}-${b.catIdx}`; }

function niceLinear(min: number, max: number, count: number): number[] {
  if (min === max) return [min];
  const range = max - min;
  const step  = niceStep(range / count);
  const start = Math.floor(min / step) * step;
  const ticks: number[] = [];
  for (let v = start; v <= max + step * 0.01; v = +(v + step).toFixed(10)) {
    ticks.push(+v.toFixed(10));
    if (ticks.length > count + 2) break;
  }
  return ticks;
}

function niceStep(rough: number): number {
  const exp  = Math.floor(Math.log10(rough));
  const frac = rough / Math.pow(10, exp);
  const nice = frac < 1.5 ? 1 : frac < 3 ? 2 : frac < 7 ? 5 : 10;
  return nice * Math.pow(10, exp);
}

function formatNum(v: number): string {
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000)     return `${(v / 1_000).toFixed(v % 1_000 === 0 ? 0 : 1)}k`;
  return String(v % 1 === 0 ? v : v.toFixed(1));
}
